/**
 * BrandScopePicker — Feb 2026 (Phase 2.1 addition to Story step)
 *
 * Lets the user pick a saved brand from `/api/brands` and multi-select
 * specific products/services under it. When Applied, the parent hydrates
 * the project's Cast & World (logos + product images + hero) from the
 * brand's context and stashes `{ brandId, productIds, serviceIds }` on
 * the project so every downstream AI call (concepts / script / co-pilot)
 * gets the same scoped brand context.
 *
 * Backend contract:
 *   GET  /api/brands                                → [{id, name, ...}, ...]
 *   GET  /api/brands/{id}/context                   → { brand, products, services,
 *                                                       assets, cast_and_world, ... }
 *   Full spec: PHASE_2_BRAND_PICKER_USER_BACKEND_GUIDE.md
 *
 * Props:
 *   value         — { brandId, productIds[], serviceIds[] } (current scope)
 *   onApply       — ({ brandId, productIds, serviceIds, castAndWorld, brand }) => void
 *   onClear       — called when the user clears the brand entirely.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Building2, Check, Loader2, X, Sparkles, RefreshCw } from 'lucide-react';

const BACKEND_URL = 'https://app.loopai.in';

// Feb 27, 2026 — HARDCODED to app.loopai.in (matches backendService.js,
// api.js, UserContext.js, ActorsLibraryModal.jsx). Previously used
// REACT_APP_BACKEND_URL which points at ads.loopai.in in production —
// that's why every /api/brands* call was 404 (the ads app doesn't serve
// brand routes, the user backend at app.loopai.in does).

// Feb 2026 — Endpoint discovery for /api/brands (user reported /api/brands
// returns 404 on production ads.loopai.in even though brand data exists in
// the user backend). Strategy:
//   1. If REACT_APP_BRANDS_LIST_ENDPOINT env var is set, use ONLY that path.
//      This lets deployments override without a code change.
//   2. Otherwise probe a broad list of common conventions in order.
// The winning path is remembered at module scope.
const BRAND_LIST_CANDIDATES = (() => {
    const override = process.env.REACT_APP_BRANDS_LIST_ENDPOINT;
    if (override && typeof override === 'string' && override.trim()) {
        return [override.trim().startsWith('/') ? override.trim() : `/${override.trim()}`];
    }
    return [
        '/api/brands',              // Ashish's spec (Feb 2026) — canonical
        '/api/brand',
        '/api/user/brands',
        '/api/users/me/brands',
        '/api/me/brands',
        '/api/user-brands',
        '/api/my-brands',
        '/api/brands/list',
        '/api/dashboard/brands',
        '/api/v1/brands',
        '/api/loopai/brands',
    ];
})();
let _resolvedBrandsBase = null;

const fetchJson = async (path) => {
    // Feb 27, 2026 — auth pattern copied verbatim from ActorsLibraryModal.jsx
    // (fetchActors). The user backend at app.loopai.in authenticates via
    // X-API-Key + X-User-Email headers stored in sessionStorage by the
    // token-exchange flow. `credentials: 'include'` alone wasn't enough
    // because the session cookie is scoped to app.loopai.in and doesn't
    // travel with cross-origin fetches from ads.loopai.in.
    const apiKey = sessionStorage.getItem('apiKey');
    const userEmail = sessionStorage.getItem('userEmail');
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey) headers['X-API-Key'] = apiKey;
    if (userEmail) headers['X-User-Email'] = userEmail;
    const res = await fetch(`${BACKEND_URL}${path}`, { headers, credentials: 'include' });
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.text()).slice(0, 160); } catch (_) { /* ignore */ }
        const err = new Error(`${res.status} ${res.statusText}${detail ? ': ' + detail : ''}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
};

/**
 * Probe every candidate path until one returns 2xx. The winning path is
 * cached so the second render doesn't re-hit the 404s. Returns
 * `{ base, data }` on success; throws the LAST error on total failure.
 */
const fetchBrandListWithFallback = async () => {
    if (_resolvedBrandsBase) {
        const data = await fetchJson(_resolvedBrandsBase);
        return { base: _resolvedBrandsBase, data };
    }
    let lastErr = null;
    for (const path of BRAND_LIST_CANDIDATES) {
        try {
            const data = await fetchJson(path);
            _resolvedBrandsBase = path;
            console.info('[BrandScopePicker] brand list endpoint resolved →', path);
            return { base: path, data };
        } catch (e) {
            lastErr = e;
            // Only fall through on 404; other errors (auth, network) surface
            // immediately because they mean "the right path but broken".
            if (e.status !== 404) throw e;
        }
    }
    throw lastErr || new Error('No brand list endpoint responded.');
};

/**
 * Feb 2026 — Parse `brand_id` / `product_ids` / `service_ids` from the
 * current URL's query string. LoopAI Admin dashboard deep-links into the
 * ad creator with these params pre-populated so the user doesn't have to
 * re-pick a brand. Returns null values when a param is missing.
 */
const readBrandScopeFromURL = () => {
    if (typeof window === 'undefined') return { brandId: null, productIds: [], serviceIds: [] };
    try {
        const p = new URLSearchParams(window.location.search);
        const parseList = (raw) => {
            if (!raw) return [];
            return raw.split(',').map(s => s.trim()).filter(Boolean);
        };
        return {
            brandId:    p.get('brand_id') || null,
            productIds: parseList(p.get('product_ids')),
            serviceIds: parseList(p.get('service_ids')),
        };
    } catch (_e) {
        return { brandId: null, productIds: [], serviceIds: [] };
    }
};

export const BrandScopePicker = ({ value = {}, onApply, onClear, aspectRatio = '16:9' }) => {
    // Feb 2026: seed initial state from URL query params (LoopAI Admin
    // deep-link) OR from the parent's `value` prop, in that order. URL
    // params take precedence because they arrive from the admin dashboard
    // where the user just explicitly picked the brand.
    const urlScope = useMemo(() => readBrandScopeFromURL(), []);
    const seedBrandId    = urlScope.brandId    || value.brandId    || null;
    const seedProductIds = urlScope.productIds.length ? urlScope.productIds : (value.productIds || []);
    const seedServiceIds = urlScope.serviceIds.length ? urlScope.serviceIds : (value.serviceIds || []);

    const [brands, setBrands] = useState([]);
    const [brandsLoading, setBrandsLoading] = useState(true);
    const [brandsError, setBrandsError] = useState(null);
    const [brandId, setBrandId] = useState(seedBrandId);
    const [ctx, setCtx] = useState(null);
    const [ctxLoading, setCtxLoading] = useState(false);
    const [pickedProducts, setPickedProducts] = useState(seedProductIds);
    const [pickedServices, setPickedServices] = useState(seedServiceIds);
    const [error, setError] = useState(null);
    // When URL supplied a brand_id, we auto-apply exactly once so downstream
    // AI calls see the scope without the user having to click "Apply brand".
    const [autoAppliedFromUrl, setAutoAppliedFromUrl] = useState(false);
    // Feb 27, 2026 — Post-apply confirmation. Once Apply fires, we show a
    // green success card summarising what was hydrated ("Fetched X products,
    // Y logos, Z world images → available in Cast & World"). Cleared when
    // the user changes brand or clears the picker.
    const [appliedSummary, setAppliedSummary] = useState(null);

    // Load brand list. Errors are surfaced in the UI — we no longer swallow
    // them silently because the previous behavior masked auth/CORS bugs.
    const loadBrands = useCallback(() => {
        let cancelled = false;
        setBrandsLoading(true);
        setBrandsError(null);
        fetchBrandListWithFallback()
            .then(({ base, data }) => {
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                setBrands(list);
                console.info('[BrandScopePicker]', base, '→', list.length, 'brand(s)');
            })
            .catch((e) => {
                if (cancelled) return;
                setBrands([]);
                const msg = e?.status === 404
                    ? `Backend returned 404 for /api/brands from this origin (${window.location.origin}). Your user backend needs to whitelist this origin for /api/brands and /api/brands/{id}/context — the same way /api/credits and /api/video-projects are already whitelisted. See /app/docs/USER_BACKEND_BRAND_ENDPOINTS_SPEC.md § "Cross-origin routing".`
                    : (e?.message || 'Could not load brands');
                setBrandsError(msg);
                console.warn('[BrandScopePicker] brand list FAILED (all candidates):', e);
            })
            .finally(() => { if (!cancelled) setBrandsLoading(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const cleanup = loadBrands();
        return cleanup;
    }, [loadBrands]);

    // Load full context when brand changes.
    useEffect(() => {
        if (!brandId) { setCtx(null); setPickedProducts([]); setPickedServices([]); return; }
        let cancelled = false;
        setCtxLoading(true); setError(null);
        // If URL provided product_ids, pass them so the backend returns the
        // scoped context. Otherwise fall back to the unscoped call.
        const qsParts = [];
        if (autoAppliedFromUrl === false && urlScope.brandId === brandId) {
            if (urlScope.productIds.length) qsParts.push(`product_ids=${encodeURIComponent(urlScope.productIds.join(','))}`);
            if (urlScope.serviceIds.length) qsParts.push(`service_ids=${encodeURIComponent(urlScope.serviceIds.join(','))}`);
        }
        const qs = qsParts.length ? `?${qsParts.join('&')}` : '';
        // Derive the /context path from whichever base successfully served
        // the brand list. Falls back to spec-canonical if unresolved.
        const contextBase = _resolvedBrandsBase || '/api/brands';
        fetchJson(`${contextBase}/${brandId}/context${qs}`)
            .then((r) => {
                if (cancelled) return;
                setCtx(r);
                console.info('[BrandScopePicker] context loaded — products:', r?.products?.length || 0, 'services:', r?.services?.length || 0);
                // Feb 27, 2026 — Default to NONE selected so the user
                // explicitly chooses which products belong in this ad.
                // Previously we pre-checked all products, which conflicted
                // with the "opt-in" mental model. URL-supplied product_ids
                // are still honoured because they arrive from LoopAI Admin
                // as an explicit selection.
                if (urlScope.brandId === brandId && urlScope.productIds.length && !autoAppliedFromUrl) {
                    setPickedProducts((prev) => prev.length ? prev : urlScope.productIds);
                    setPickedServices((prev) => prev.length ? prev : urlScope.serviceIds);
                } else {
                    setPickedProducts([]);
                    setPickedServices([]);
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setCtx(null);
                setError(e?.message || 'Failed to load brand.');
                console.warn('[BrandScopePicker] /api/brands/{id}/context FAILED:', e);
            })
            .finally(() => { if (!cancelled) setCtxLoading(false); });
        return () => { cancelled = true; };
    // Only rerun when brandId changes; urlScope is stable (parsed once).
    }, [brandId]);

    // If the user changes brand or product selection after applying, dim
    // the summary so it doesn't lie about the current scope.
    useEffect(() => { setAppliedSummary(null); }, [brandId, pickedProducts, pickedServices]);

    // Feb 13 2026 — Product-sheet resolution. The user-backend
    // `/api/brands/{id}/context` response includes a `sheets` map per
    // product keyed by aspect-ratio (e.g. `{"16:9":"…", "9:16":"…"}`).
    // For every picked product we look up the sheet matching the
    // project's aspect ratio and use IT as the product's image in Cast
    // & World — instead of the raw multi-image gallery. If a sheet at
    // the requested aspect ratio is missing, that product is DROPPED
    // from Cast & World and surfaced in `productsMissingSheet` so the
    // apply banner can warn the user.
    const { scopedCastAndWorld, productsMissingSheet } = useMemo(() => {
        if (!ctx) return { scopedCastAndWorld: { logos: [], products: [], world: [] }, productsMissingSheet: [] };

        const missing = [];
        const productSheetEntries = [];

        for (const productId of pickedProducts) {
            const product = (ctx.products || []).find(p => p.id === productId);
            if (!product) continue;
            // Support several plausible shapes for the sheets field so we
            // don't die on backend variations: sheets can be either
            //   { "16:9": "url", "9:16": "url" }
            // or an ARRAY like [{ aspect_ratio: "16:9", url: "…" }, …].
            let sheetUrl = null;
            const raw = product.sheets || product.product_sheets || product.sheet_urls;
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
                // Direct key lookup + fallback aliases for the ratio string.
                const aliases = [aspectRatio, aspectRatio.replace(':', 'x'), aspectRatio.replace(':', '_')];
                for (const a of aliases) {
                    if (raw[a] && typeof raw[a] === 'string') { sheetUrl = raw[a]; break; }
                    // case-insensitive scan
                    const matchedKey = Object.keys(raw).find(k => k.toLowerCase() === a.toLowerCase());
                    if (matchedKey && typeof raw[matchedKey] === 'string') { sheetUrl = raw[matchedKey]; break; }
                }
            } else if (Array.isArray(raw)) {
                const entry = raw.find(s =>
                    (s?.aspect_ratio || s?.aspectRatio || s?.ratio || '').toString().toLowerCase() === aspectRatio.toLowerCase()
                );
                sheetUrl = entry?.url || entry?.image_url || entry?.src || null;
            }

            if (sheetUrl) {
                productSheetEntries.push({
                    id: `sheet-${productId}`,
                    product_id: productId,
                    url: sheetUrl,
                    name: product.name,
                    label: `${product.name} — product sheet`,
                    aspect_ratio: aspectRatio,
                    is_product_sheet: true,
                });
            } else {
                missing.push({ id: productId, name: product.name || productId });
            }
        }

        return {
            scopedCastAndWorld: {
                logos:    ctx.cast_and_world?.logos    || [],
                products: productSheetEntries,
                world:    ctx.cast_and_world?.world    || [],
            },
            productsMissingSheet: missing,
        };
    }, [ctx, pickedProducts, aspectRatio]);

    const handleApply = () => {
        // Compute the picked products' + services' full records (name,
        // description, images) so the parent can pass rich brand context
        // to concept / script generation instead of just IDs. Without this,
        // the LLM only sees the brand name and produces generic concepts.
        const pickedProductsFull = (ctx?.products || [])
            .filter((p) => pickedProducts.includes(p.id));
        const pickedServicesFull = (ctx?.services || [])
            .filter((s) => pickedServices.includes(s.id));
        const summary = {
            brandName:     ctx?.brand?.name || 'brand',
            productCount:  pickedProducts.length,
            serviceCount:  pickedServices.length,
            logoCount:     scopedCastAndWorld.logos.length,
            productImages: scopedCastAndWorld.products.length,     // now = number of sheets loaded
            worldImages:   scopedCastAndWorld.world.length,
            appliedAt:     Date.now(),
            aspectRatio,
            productsMissingSheet,                                  // Feb 13 2026 — surface missing-sheet warning
        };
        onApply?.({
            brandId,
            productIds: pickedProducts,
            serviceIds: pickedServices,
            castAndWorld: scopedCastAndWorld,
            brand: ctx?.brand,
            pickedProductsFull,
            pickedServicesFull,
            productsMissingSheet,
            aspectRatio,
        });
        setAppliedSummary(summary);
        console.info('[BrandScopePicker] Applied →', summary, '· sheets found:', scopedCastAndWorld.products.length, '· missing at', aspectRatio + ':', productsMissingSheet.map(p => p.name));
    };

    const clearAppliedSummary = () => setAppliedSummary(null);

    // Feb 2026 — auto-apply the scope exactly once when the URL supplied a
    // brand_id and the context has just finished loading. Users who arrive
    // via the LoopAI Admin dashboard's deep-link shouldn't have to click
    // "Apply brand" — the scope is already the one they picked upstream.
    useEffect(() => {
        if (autoAppliedFromUrl) return;
        if (!urlScope.brandId || urlScope.brandId !== brandId) return;
        if (!ctx || !onApply) return;
        const pickedProductsFull = (ctx?.products || [])
            .filter((p) => pickedProducts.includes(p.id));
        const pickedServicesFull = (ctx?.services || [])
            .filter((s) => pickedServices.includes(s.id));
        onApply({
            brandId,
            productIds: pickedProducts,
            serviceIds: pickedServices,
            castAndWorld: scopedCastAndWorld,
            brand: ctx.brand,
            pickedProductsFull,
            pickedServicesFull,
            productsMissingSheet,
            aspectRatio,
        });
        setAutoAppliedFromUrl(true);
        console.info('[BrandScopePicker] auto-applied brand from URL:', brandId, '· sheets found:', scopedCastAndWorld.products.length, '· missing:', productsMissingSheet.length);
    }, [ctx, brandId, urlScope.brandId, autoAppliedFromUrl]);

    const handleClear = () => {
        setBrandId(null);
        setCtx(null);
        setPickedProducts([]);
        setPickedServices([]);
        setAppliedSummary(null);
        onClear?.();
    };

    // Feb 2026 — Always render the picker so users always see the brand
    // selection surface. Previously we silent-hid it when brands.length===0
    // which made the feature invisible for brand-less accounts and made
    // Story-step regressions look identical to "we forgot to deploy".
    const hasBrands = brands.length > 0;

    return (
        <div className="rounded-2xl border border-violet-500/25 bg-violet-500/[0.04] p-4 space-y-3" data-testid="brand-scope-picker">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-violet-300" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">Pick a saved brand <span className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold ml-1">optional</span></div>
                    <div className="text-[11px] text-zinc-400">Loads logo, product images and tone-of-voice straight into your project.</div>
                </div>
                <button type="button" onClick={loadBrands}
                    disabled={brandsLoading}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-40"
                    data-testid="brand-scope-refresh-btn"
                    title="Refresh brands list">
                    <RefreshCw className={`w-3.5 h-3.5 ${brandsLoading ? 'animate-spin' : ''}`} />
                </button>
                {brandId && (
                    <button type="button" onClick={handleClear}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5"
                        data-testid="brand-scope-clear-btn"
                        title="Clear brand">
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {/* Surface any brands-list error so we can diagnose auth/CORS issues */}
            {brandsError && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200" data-testid="brand-scope-brands-error">
                    Couldn&apos;t load brands: {brandsError}. If you&apos;re signed in and expect to see brands here, please refresh (⟳) or check your session.
                </div>
            )}

            {/* Post-apply confirmation banner (Feb 27, 2026) */}
            {appliedSummary && (
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08] px-3 py-3 flex items-start gap-2.5" data-testid="brand-scope-applied-summary">
                    <div className="w-7 h-7 flex-shrink-0 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                    </div>
                    <div className="flex-1 min-w-0 text-[12px] leading-snug">
                        <div className="font-semibold text-white">
                            Applied · {appliedSummary.brandName}
                        </div>
                        <div className="text-zinc-300 mt-0.5">
                            Fetched <span className="text-white font-semibold">{appliedSummary.productCount}</span> product{appliedSummary.productCount === 1 ? '' : 's'}
                            {appliedSummary.serviceCount > 0 && <>, <span className="text-white font-semibold">{appliedSummary.serviceCount}</span> service{appliedSummary.serviceCount === 1 ? '' : 's'}</>}
                            . Loaded <span className="text-white font-semibold">{appliedSummary.logoCount}</span> logo{appliedSummary.logoCount === 1 ? '' : 's'}, <span className="text-white font-semibold">{appliedSummary.productImages}</span> product sheet{appliedSummary.productImages === 1 ? '' : 's'} @ {appliedSummary.aspectRatio || aspectRatio}
                            {appliedSummary.worldImages > 0 && <>, <span className="text-white font-semibold">{appliedSummary.worldImages}</span> world image{appliedSummary.worldImages === 1 ? '' : 's'}</>}
                            {' '}→ available in Cast & World.
                        </div>
                        {Array.isArray(appliedSummary.productsMissingSheet) && appliedSummary.productsMissingSheet.length > 0 && (
                            <div
                                className="text-[11px] text-amber-200 mt-1.5 leading-snug"
                                data-testid="brand-scope-applied-missing-sheet"
                            >
                                <span className="font-semibold">Note:</span> no {appliedSummary.aspectRatio || aspectRatio} product sheet for{' '}
                                <span className="font-semibold">{appliedSummary.productsMissingSheet.map(p => p.name).join(', ')}</span>.
                                {' '}These products weren&apos;t loaded into Cast &amp; World — please upload sheets for this aspect ratio in the LoopAI dashboard.
                            </div>
                        )}
                        <div className="text-[10px] text-emerald-200/80 mt-1.5">
                            Brand details will guide concept, script and co-pilot generation for this project.
                        </div>
                    </div>
                    <button type="button" onClick={clearAppliedSummary}
                        className="p-1 rounded text-emerald-300/60 hover:text-emerald-200"
                        data-testid="brand-scope-applied-dismiss" title="Dismiss">
                        <X className="w-3 h-3" />
                    </button>
                </div>
            )}

            {/* Brand chips OR empty state */}
            {brandsLoading ? (
                <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading brands…</div>
            ) : hasBrands ? (
                <div className="flex flex-wrap gap-2">
                    {brands.map((b) => {
                        const isActive = brandId === b.id;
                        return (
                            <button
                                key={b.id}
                                type="button"
                                onClick={() => setBrandId(isActive ? null : b.id)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                    isActive
                                        ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                                        : 'bg-white/[0.04] text-zinc-200 border border-white/10 hover:bg-white/[0.08]'
                                }`}
                                data-testid={`brand-scope-option-${b.id}`}
                            >
                                {isActive && <Check className="w-3 h-3" />}
                                {b.name}
                            </button>
                        );
                    })}
                </div>
            ) : urlScope.brandId ? (
                // Deep-linked from LoopAI Admin — /api/brands may have failed
                // but we still have the brand_id from URL. Show a "using brand
                // from URL" chip while the context finishes loading.
                <div className="text-[11px] text-violet-200 inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 border border-violet-500/40 px-3 py-1.5" data-testid="brand-scope-url-badge">
                    <Check className="w-3 h-3" />
                    Using brand from URL: <code className="font-mono text-[10px] text-violet-300">{urlScope.brandId.slice(0, 8)}…</code>
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-3 py-3 flex items-start gap-2" data-testid="brand-scope-empty">
                    <Sparkles className="w-3.5 h-3.5 text-violet-300 flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-zinc-400 leading-relaxed">
                        No saved brands yet. Add a brand + your products in the LoopAI brand dashboard and they&apos;ll show up here — one click to hydrate logo, product images and tone of voice into your ad.
                    </div>
                </div>
            )}

            {ctxLoading && (
                <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading brand context…
                </div>
            )}

            {error && (
                <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2 py-1.5">{error}</div>
            )}

            {ctx && !ctxLoading && (
                <>
                    {/* Brand preview strip */}
                    <div className="flex items-center gap-2 pt-1 border-t border-violet-500/15">
                        {ctx.assets?.logo_url && (
                            <img src={ctx.assets.logo_url} alt="" className="w-6 h-6 rounded object-cover bg-white/10" />
                        )}
                        <div className="text-xs text-zinc-300 flex-1 min-w-0 truncate">
                            <span className="font-semibold text-white">{ctx.brand?.name}</span>
                            {ctx.brand?.profile?.communication?.tone_of_voice && (
                                <span className="text-zinc-500 ml-2 truncate">· tone: {ctx.brand.profile.communication.tone_of_voice}</span>
                            )}
                        </div>
                    </div>

                    {/* Products */}
                    {(ctx.products?.length || 0) > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-violet-300 font-bold mb-1.5">Products ({ctx.products.length})</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
                                {ctx.products.map((p) => {
                                    const on = pickedProducts.includes(p.id);
                                    const thumb = Array.isArray(p.images) && p.images.length
                                        ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.url)
                                        : null;
                                    return (
                                        <label
                                            key={p.id}
                                            data-testid={`brand-scope-product-${p.id}`}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${
                                                on
                                                    ? 'bg-violet-500/15 border border-violet-500/40'
                                                    : 'bg-white/[0.02] border border-white/10 hover:border-white/25'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={(e) => setPickedProducts((prev) =>
                                                    e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id)
                                                )}
                                                className="w-3.5 h-3.5 accent-violet-500"
                                            />
                                            {thumb ? (
                                                <img src={thumb} alt="" className="w-7 h-7 rounded object-cover" />
                                            ) : (
                                                <div className="w-7 h-7 rounded bg-violet-500/15 border border-violet-500/30" />
                                            )}
                                            <span className="flex-1 truncate text-zinc-200">{p.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Services */}
                    {(ctx.services?.length || 0) > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-violet-300 font-bold mb-1.5">Services ({ctx.services.length})</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto pr-1">
                                {ctx.services.map((s) => {
                                    const on = pickedServices.includes(s.id);
                                    return (
                                        <label
                                            key={s.id}
                                            data-testid={`brand-scope-service-${s.id}`}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${
                                                on
                                                    ? 'bg-violet-500/15 border border-violet-500/40'
                                                    : 'bg-white/[0.02] border border-white/10 hover:border-white/25'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={(e) => setPickedServices((prev) =>
                                                    e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                                                )}
                                                className="w-3.5 h-3.5 accent-violet-500"
                                            />
                                            <span className="flex-1 truncate text-zinc-200">{s.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Apply footer */}
                    <div className="pt-2 border-t border-violet-500/15 space-y-2">
                        {productsMissingSheet.length > 0 && (
                            <div className="text-[10px] text-amber-300 flex items-start gap-1.5" data-testid="brand-scope-missing-sheet-warning">
                                <Sparkles className="w-3 h-3 text-amber-300 mt-[2px] flex-shrink-0" />
                                <span>
                                    No {aspectRatio} product sheet for:{' '}
                                    <span className="font-semibold">{productsMissingSheet.map(p => p.name).join(', ')}</span>.
                                    These products won&apos;t be loaded into Cast &amp; World.
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] text-zinc-400 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-violet-300" />
                                Will hydrate {scopedCastAndWorld.logos.length} logo, {scopedCastAndWorld.products.length} product sheet{scopedCastAndWorld.products.length === 1 ? '' : 's'} @ {aspectRatio}
                                {scopedCastAndWorld.world.length > 0 ? ', 1 world image' : ''}.
                            </div>
                            <button
                                type="button"
                                onClick={handleApply}
                                disabled={!brandId || (pickedProducts.length === 0 && pickedServices.length === 0)}
                                className="px-3 py-1.5 rounded-full bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-bold transition-colors"
                                data-testid="brand-scope-apply"
                            >
                                Apply brand
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default BrandScopePicker;
